import { Button, Typography } from 'components';
import { CheckCircleOutlinedIcon, WarningOutlinedIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useInvoiceDownload } from 'providers/InvoicesProviders/useInvoiceDownload';
import { useInvoiceFile } from 'providers/InvoicesProviders/useInvoiceFile';
import { useCallback } from 'react';
import { cn } from 'utils/cn';
import { dateTimeShort } from 'utils/time/dateTimeShort';
import InvoiceContent from './invoice_partials/InvoiceContent';

export default function Invoice({ invoice }) {
  const t = useTranslations();

  const router = useRouter();

  const transactionsUrl = `/billing/transactions?invoiceId=${invoice.id}`;

  const handlerOnClick = () =>
    (window.location.href = `/billing/transactions?invoiceId=${invoice.id}&sortOrder=%22desc%22&sortAttribute=%22created_at%22`);

  router.prefetch(transactionsUrl);

  const { mutateAsync: getInvoiceFile } = useInvoiceFile();
  const { mutateAsync: downloadInvoice } = useInvoiceDownload();

  const handlerGetInvoiceFile = useCallback(() => {
    if (!invoice.id) return;

    downloadInvoice({ invoiceId: invoice.id }).then((response) => {
      getInvoiceFile(response.downloadId);
    });
  }, [downloadInvoice, getInvoiceFile, invoice.id]);

  const isPaid = invoice.paid;

  return (
    <div className="rounded-md font-content shadow-custom">
      <div className="dont_print">
        <div className="flex h-[64px] items-center justify-between rounded-md bg-white px-10">
          <Typography variant="medium1" className="text-tertiary-100">
            {t('invoices.invoice')} #{invoice.id}
          </Typography>
          <span
            className={cn(
              'flex items-center justify-center rounded-[50px] px-3',
              isPaid
                ? 'bg-primary-green-300 text-primary-green-100'
                : 'bg-primary-orange-1200 text-primary-orange-100'
            )}
          >
            <Typography
              variant="medium4"
              className={cn(isPaid ? 'text-primary-green-100' : 'text-primary-orange-100')}
            >
              {isPaid ? t('invoices.paid') : t('invoices.unpaid')}
            </Typography>
          </span>
        </div>
        <div
          className={cn(
            'flex h-[40px] items-center px-9',
            isPaid
              ? 'border-0 border-b-1 border-primary-green-100 bg-primary-green-300 text-primary-green-100'
              : 'border-0 border-b-1 border-primary-orange-200 bg-primary-orange-1200 text-primary-orange-100'
          )}
        >
          <Typography variant="regular4" className={cn('flex text-tertiary-100')}>
            {isPaid && (
              <span className="ml-1 mr-2">
                <CheckCircleOutlinedIcon className="mt-1 scale-[1.2]" />
              </span>
            )}
            {!isPaid && (
              <WarningOutlinedIcon className="mr-1 scale-[.8] !text-primary-orange-100" />
            )}

            {isPaid
              ? t('invoices.paid_on', { date: dateTimeShort(invoice.issuedAt) })
              : t('invoices.unpaid_since', { date: dateTimeShort(invoice.dueDate) })}
          </Typography>
        </div>
      </div>
      <div className="invoices__paper">
        <InvoiceContent invoice={invoice} />
      </div>
      <div className="dont_print mb-20 flex justify-end gap-4 rounded-b-md border-0 border-t-1 border-t-tertiary-600 bg-white p-4">
        <Button
          label={t('billing.show_transactions')}
          onClick={handlerOnClick}
          size="large"
          className="w-1/2"
          variant="contained"
        />
        <Button
          color="secondary"
          label={t('billing.print')}
          onClick={handlerGetInvoiceFile}
          size="large"
          className="w-1/2"
          variant="contained"
        />
      </div>
    </div>
  );
}
